/**
 * SetterManagementList Component
 *
 * Displays a list of setters at a gym and allows the owner to remove them.
 * Shows setter name, email, joined date, and route count.
 */

import { useState, useEffect, useCallback, type CSSProperties } from 'react';
import { collection, query, where, getDocs, type DocumentData } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseDb, getFirebaseFunctions } from '@/lib/firebase';
import type { User, GymRole } from '@flash/shared';

/**
 * Props for SetterManagementList component
 */
interface SetterManagementListProps {
  /** Gym ID to list setters for */
  gymId: string;
}

/**
 * Extended setter data with user details and route count
 */
interface SetterInfo {
  userId: string;
  displayName: string;
  email: string;
  role: GymRole;
  joinedAt: Date;
  routeCount: number;
}

/**
 * SetterManagementList displays and manages setters at a gym
 */
export const SetterManagementList = ({ gymId }: SetterManagementListProps) => {
  const [setters, setSetters] = useState<SetterInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  /**
   * Fetch setters data
   */
  const fetchSetters = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const db = getFirebaseDb();

      // 1. Get all memberships with setter or owner role at this gym
      const membershipsRef = collection(db, 'gymMemberships');
      const membershipsQuery = query(membershipsRef, where('gymId', '==', gymId));
      const membershipsSnapshot = await getDocs(membershipsQuery);

      // Filter to setters and owners
      const setterMemberships: Array<{ membership: DocumentData; userId: string }> = [];
      membershipsSnapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        const role = data['role'] as GymRole;
        if (role === 'setter' || role === 'owner') {
          setterMemberships.push({
            membership: data,
            userId: data['userId'] as string,
          });
        }
      });

      if (setterMemberships.length === 0) {
        setSetters([]);
        setLoading(false);
        return;
      }

      // 2. Get user details for each setter
      const userIds = setterMemberships.map((s) => s.userId);
      const usersRef = collection(db, 'users');

      // Firestore 'in' queries are limited to 30 items
      const userBatches: string[][] = [];
      for (let i = 0; i < userIds.length; i += 30) {
        userBatches.push(userIds.slice(i, i + 30));
      }

      const userMap = new Map<string, User>();
      for (const batch of userBatches) {
        const usersQuery = query(usersRef, where('id', 'in', batch));
        const usersSnapshot = await getDocs(usersQuery);
        usersSnapshot.docs.forEach((docSnapshot) => {
          const userData = docSnapshot.data() as User;
          userMap.set(userData.id, userData);
        });
      }

      // 3. Get route counts for each setter
      const routesRef = collection(db, 'routes');
      const routeCountMap = new Map<string, number>();

      for (const { userId } of setterMemberships) {
        const routesQuery = query(
          routesRef,
          where('gymId', '==', gymId),
          where('setterId', '==', userId)
        );
        const routesSnapshot = await getDocs(routesQuery);
        routeCountMap.set(userId, routesSnapshot.size);
      }

      // 4. Combine all data
      const setterInfoList: SetterInfo[] = setterMemberships.map(({ membership, userId }) => {
        const user = userMap.get(userId);
        const createdAt = membership['createdAt'];
        const joinedAt =
          createdAt && 'toDate' in createdAt
            ? createdAt.toDate()
            : createdAt instanceof Date
              ? createdAt
              : new Date();

        return {
          userId,
          displayName: user?.displayName ?? 'Unknown User',
          email: user?.email ?? '',
          role: membership['role'] as GymRole,
          joinedAt,
          routeCount: routeCountMap.get(userId) ?? 0,
        };
      });

      // Sort: owners first, then by name
      setterInfoList.sort((a, b) => {
        if (a.role === 'owner' && b.role !== 'owner') return -1;
        if (b.role === 'owner' && a.role !== 'owner') return 1;
        return a.displayName.localeCompare(b.displayName);
      });

      setSetters(setterInfoList);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load setters';
      setError(errorMessage);
      console.error('Error fetching setters:', err);
    } finally {
      setLoading(false);
    }
  }, [gymId]);

  // Load setters on mount and when gymId changes
  useEffect(() => {
    fetchSetters();
  }, [fetchSetters]);

  /**
   * Initiate setter removal
   */
  const handleRemoveClick = (userId: string) => {
    setRemovingUserId(userId);
    setRemoveError(null);
  };

  /**
   * Cancel removal
   */
  const handleCancelRemove = () => {
    setRemovingUserId(null);
    setRemoveError(null);
  };

  /**
   * Confirm and execute removal
   */
  const handleConfirmRemove = async () => {
    if (!removingUserId) return;

    setRemoveLoading(true);
    setRemoveError(null);

    try {
      const functions = getFirebaseFunctions();
      const removeSetterFn = httpsCallable(functions, 'removeSetter');

      await removeSetterFn({ gymId, userId: removingUserId });

      // Refresh the list
      setRemovingUserId(null);
      await fetchSetters();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove setter';
      setRemoveError(errorMessage);
      console.error('Error removing setter:', err);
    } finally {
      setRemoveLoading(false);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get setter being removed
  const setterToRemove = removingUserId ? setters.find((s) => s.userId === removingUserId) : null;

  if (loading) {
    return <div style={loadingStyle}>Loading setters...</div>;
  }

  if (error) {
    return (
      <div style={errorContainerStyle}>
        <p style={errorTextStyle}>{error}</p>
        <button onClick={fetchSetters} style={retryButtonStyle}>
          Try Again
        </button>
      </div>
    );
  }

  if (setters.length === 0) {
    return (
      <div style={emptyStateStyle}>
        <p style={emptyStateTextStyle}>No setters at this gym yet.</p>
        <p style={emptyStateSubtextStyle}>
          Invite someone to become a setter using the form above.
        </p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Setters List */}
      <div style={listStyle}>
        {setters.map((setter) => (
          <div key={setter.userId} style={setterItemStyle}>
            <div style={setterInfoStyle}>
              <div style={setterHeaderStyle}>
                <span style={setterNameStyle}>{setter.displayName}</span>
                {setter.role === 'owner' && <span style={ownerBadgeStyle}>Owner</span>}
              </div>
              <span style={setterEmailStyle}>{setter.email}</span>
              <div style={setterStatsStyle}>
                <span style={statStyle}>Joined: {formatDate(setter.joinedAt)}</span>
                <span style={statStyle}>Routes: {setter.routeCount}</span>
              </div>
            </div>

            {/* Remove button - hidden for owner */}
            {setter.role !== 'owner' && (
              <button
                type="button"
                onClick={() => handleRemoveClick(setter.userId)}
                style={removeButtonStyle}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Removal Confirmation Dialog */}
      {removingUserId && setterToRemove && (
        <div style={overlayStyle}>
          <div style={dialogStyle}>
            <h3 style={dialogTitleStyle}>Remove Setter</h3>
            <p style={dialogTextStyle}>
              Are you sure you want to remove <strong>{setterToRemove.displayName}</strong> as a
              setter?
            </p>
            <p style={dialogSubtextStyle}>
              They will be demoted to a regular climber and will no longer be able to create or edit
              routes at this gym.
            </p>

            {setterToRemove.routeCount > 0 && (
              <p style={warningTextStyle}>
                Note: Their {setterToRemove.routeCount} existing route
                {setterToRemove.routeCount !== 1 ? 's' : ''} will remain attributed to them.
              </p>
            )}

            {removeError && <div style={dialogErrorStyle}>{removeError}</div>}

            <div style={dialogActionsStyle}>
              <button
                type="button"
                onClick={handleCancelRemove}
                disabled={removeLoading}
                style={cancelButtonStyle}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRemove}
                disabled={removeLoading}
                style={{
                  ...confirmRemoveButtonStyle,
                  ...(removeLoading ? disabledButtonStyle : {}),
                }}
              >
                {removeLoading ? 'Removing...' : 'Remove Setter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Component styles using CSS variables for dark mode support
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const loadingStyle: CSSProperties = {
  padding: '20px',
  textAlign: 'center',
  color: 'var(--color-text-secondary)',
  fontSize: '0.875rem',
};

const errorContainerStyle: CSSProperties = {
  padding: '20px',
  textAlign: 'center',
  backgroundColor: 'var(--color-badge-warning-bg)',
  borderRadius: '8px',
  border: '1px solid var(--color-error)',
};

const errorTextStyle: CSSProperties = {
  color: 'var(--color-error)',
  fontSize: '0.875rem',
  marginBottom: '12px',
};

const retryButtonStyle: CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-error)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const emptyStateStyle: CSSProperties = {
  padding: '32px 20px',
  textAlign: 'center',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
  border: '1px dashed var(--color-border)',
};

const emptyStateTextStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: '0.875rem',
  marginBottom: '4px',
};

const emptyStateSubtextStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: '0.75rem',
};

const listStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const setterItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
  border: '1px solid var(--color-border)',
};

const setterInfoStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  flex: 1,
};

const setterHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const setterNameStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};

const ownerBadgeStyle: CSSProperties = {
  fontSize: '0.625rem',
  fontWeight: 600,
  color: 'var(--color-badge-success-text)',
  backgroundColor: 'var(--color-badge-success-bg)',
  padding: '2px 6px',
  borderRadius: '4px',
  textTransform: 'uppercase',
};

const setterEmailStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary)',
};

const setterStatsStyle: CSSProperties = {
  display: 'flex',
  gap: '16px',
  marginTop: '4px',
};

const statStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted)',
};

const removeButtonStyle: CSSProperties = {
  padding: '8px 14px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  color: 'var(--color-error)',
  border: '1px solid var(--color-error)',
  borderRadius: '6px',
  fontSize: '0.75rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const overlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px',
};

const dialogStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  padding: '24px',
  maxWidth: '400px',
  width: '100%',
};

const dialogTitleStyle: CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: '12px',
};

const dialogTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '8px',
};

const dialogSubtextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted)',
  marginBottom: '12px',
};

const warningTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-warning)',
  backgroundColor: 'var(--color-badge-warning-bg)',
  padding: '10px',
  borderRadius: '6px',
  marginBottom: '16px',
};

const dialogErrorStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-error)',
  backgroundColor: 'var(--color-badge-warning-bg)',
  padding: '10px',
  borderRadius: '6px',
  marginBottom: '12px',
};

const dialogActionsStyle: CSSProperties = {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
};

const cancelButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const confirmRemoveButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'var(--color-error)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const disabledButtonStyle: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

export default SetterManagementList;
